import {AxiosError, AxiosResponse} from "axios";
import {injectable} from "inversify";

import "@/extensions/date.extensions";
import {ApiService} from "@/services/apiService";
import {IContactService} from "@/injection/interfaces";
import {SucceededOrNotResponse} from "@/types/responses/succeededOrNotResponse";
import {IAddContactRequest} from "@/types/requests/addContactRequest";
import {Contact} from "@/types/entities/contact";
import {useMemberStore} from "@/stores/memberStore";
import {Role} from "@/types";

@injectable()
export class ContactService extends ApiService implements IContactService {
  public async getContact(organizationId: string, contactId: string): Promise<Contact> {
    const memberStore = useMemberStore();
    const endpointUrl = memberStore.hasRole(Role.Admin)
      ? `${process.env.VUE_APP_API_BASE_URL}/api/organizations/${organizationId}/contacts/${contactId}`
      : `${process.env.VUE_APP_API_BASE_URL}/api/organizations/mine/contacts/${contactId}`
    const response = await this
      ._httpClient
      .get<AxiosResponse<Contact>>(endpointUrl)
      .catch(function (error: AxiosError): AxiosResponse<Contact> {
        return error.response as AxiosResponse<Contact>;
      });
    return response.data as Contact
  }

  public async addContact(request: IAddContactRequest, organizationId: string): Promise<SucceededOrNotResponse> {
    const memberStore = useMemberStore();
    const endpointUrl = memberStore.hasRole(Role.Admin)
      ? `${process.env.VUE_APP_API_BASE_URL}/api/organizations/${organizationId}/contacts`
      : `${process.env.VUE_APP_API_BASE_URL}/api/organizations/mine/contacts`
    const response = await this._httpClient
      .post<IAddContactRequest, AxiosResponse<SucceededOrNotResponse>>(endpointUrl, request)
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>;
      });
    const succeededOrNotResponse = response.data as SucceededOrNotResponse
    return new SucceededOrNotResponse(succeededOrNotResponse.succeeded, succeededOrNotResponse.errors)
  }

  public async updateContact(contact: Contact, organizationId: string): Promise<SucceededOrNotResponse> {
    const memberStore = useMemberStore();
    const endpointUrl = memberStore.hasRole(Role.Admin)
      ? `${process.env.VUE_APP_API_BASE_URL}/api/organizations/${organizationId}/contacts/${contact.id}`
      : `${process.env.VUE_APP_API_BASE_URL}/api/organizations/mine/contacts/${contact.id}`
    const response = await this
      ._httpClient
      .put<Contact, AxiosResponse<SucceededOrNotResponse>>(endpointUrl, contact)
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>;
      });
    const succeededOrNotResponse = response.data as SucceededOrNotResponse
    return new SucceededOrNotResponse(succeededOrNotResponse.succeeded, succeededOrNotResponse.errors)
  }

  public async deleteContact(contactId: string, organizationId: string): Promise<SucceededOrNotResponse> {
    const memberStore = useMemberStore()
    const endpointUrl = memberStore.hasRole(Role.Admin)
      ? `${process.env.VUE_APP_API_BASE_URL}/api/organizations/${organizationId}/contacts/${contactId}`
      : `${process.env.VUE_APP_API_BASE_URL}/api/organizations/mine/contacts/${contactId}`
    const response = await this
      ._httpClient
      .delete<AxiosResponse<any>>(endpointUrl)
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>
      })
    return new SucceededOrNotResponse(response.status === 204)
  }
}